import * as Colyseus from "colyseus.js";
import { ACTION } from "@hupper/shared/protocol";
import { GLASS_PANE_AABB } from "@hupper/shared";
import { getWeapon, weaponSlotFor } from "@hupper/shared/weapons";

/**
 * In-browser practice opponent — the browser port of scripts/bot.mjs
 * (same brain: arena pathfinding + LOS checks, imperfect reaction-limited
 * tap-fire, strafe band, unstuck wiggle; minus the video-recording chat /
 * surrender machinery and the node CLI).
 *
 * Spawned by main.ts for direct play.hupper.fun visitors: it opens a
 * SECOND Colyseus connection from the visitor's own tab into the same
 * `fps_practice` room (paired via the shared random `practiceId`), so the
 * server needs no bot logic at all and the bot's CPU is the visitor's.
 *
 * Contract notes (same as bot.mjs):
 *   - Never declares `readyGateV2` — the asset-load gate must only wait
 *     on the human; the bot has no assets to load.
 *   - All gameplay rides plain messages (`input`, `snapshot`, `pong`);
 *     the room's schema state is ignored (its decoder noise is the same
 *     harmless chatter the main client already produces).
 *   - Background-tab timer throttling only ever slows the HUMAN's own
 *     bot down while they are not looking — acceptable by construction.
 */

interface Box {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

interface BotPlayerSnap {
  sessionId: string;
  x: number; y: number; z: number;
  health: number;
  team?: "ct" | "t";
  weaponId: string;
  /** Round economy — drives the buy-phase loadout decisions. */
  money?: number;
  ownedWeapons?: string[];
  armor?: number;
  helmet?: boolean;
}

interface BotSnapshotMsg {
  phase: string;
  glassBroken?: boolean;
  players?: BotPlayerSnap[];
}

/** Movement is server-integrated only in these phases (look always works). */
const MOVE_PHASES = new Set(["live", "warmup", "between_rounds"]);
/** Combat is live-only so the bot never shoots at a loading/buy screen. */
const FIRE_PHASES = new Set(["live", "between_rounds"]);

const RAD2DEG = 180 / Math.PI;
const DEG2RAD = Math.PI / 180;
const COMBAT_NEAR = 6, COMBAT_FAR = 15; // strafe band while shooting

const gauss = (): number => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;

/** Best-first equip priority — rifles over SMGs over the Deagle over the
 *  starter pistols. AWP is deliberately absent (the bot has no scope/zoom
 *  behavior, so it neither buys nor equips one). */
const WEAPON_PRIORITY = ["ak47", "m4a1_s", "m4a4", "mac10", "mp9", "deagle", "dualies", "usp_s", "g18"] as const;

/** Engagement tuning per weapon class: how far the bot opens fire and how
 *  fast it taps. Skill (0..1) scales cadence the same way it always scaled
 *  the pistol. Values stay conservative — the bot should feel stronger with
 *  a rifle, not aimbot-fast. */
function fireParamsFor(weaponId: string, skill: number): { range: number; tapsPerSec: number } {
  let cls = "pistol";
  try {
    cls = getWeapon(weaponId).class;
  } catch { /* unknown id → pistol handling */ }
  if (weaponId === "deagle") return { range: 30, tapsPerSec: 1.4 + skill * 1.2 };
  switch (cls) {
    case "rifle": return { range: 34, tapsPerSec: 3.5 + skill * 2.5 };
    case "smg": return { range: 24, tapsPerSec: 4.5 + skill * 3.5 };
    case "sniper": return { range: 40, tapsPerSec: 0.6 + skill * 0.4 };
    default: return { range: 26, tapsPerSec: 2 + skill * 2 }; // pistols
  }
}

export class PracticeBot {
  private room: Colyseus.Room | null = null;
  private brainTimer: number | null = null;
  private disposed = false;

  // Room state from snapshots.
  private players: BotPlayerSnap[] = [];
  private phase = "";
  private glassBroken = false;

  // Brain state.
  private seq = 0;
  private yaw = 0;
  private pitch = 0;
  private path: Array<{ x: number; z: number }> | null = null;
  private lastPathAt = 0;
  private errYaw = 0;
  private errPitch = 0;
  private losSince: number | null = null;
  private nextShotAt = 0;
  private strafeSide = 1;
  private nextStrafeFlipAt = 0;
  private lastEquipSentAt = 0;
  private anchor: { x: number; z: number; t: number } | null = null;
  private episode: { until: number; jumpUntil: number; side: "left" | "right" } | null = null;
  private episodeCount = 0;
  /** One loadout decision per buy phase; re-armed whenever phase ≠ buy. */
  private boughtThisBuyPhase = false;

  // Skill knobs (0..1): reaction time, aim wander, tap rate.
  private readonly skill: number;
  private readonly reactionMs: number;
  private readonly aimErrMaxDeg: number;

  private readonly solids: Box[];
  private readonly glassBox: Box;
  private readonly nav: {
    findPath: (fromX: number, fromZ: number, toX: number, toZ: number) => Array<{ x: number; z: number }> | null;
  };

  constructor(
    private readonly serverUrl: string,
    private readonly practiceId: string,
    arenaBoxes: ReadonlyArray<Box>,
    private readonly name = "HUPPER_BOT",
    skill = 0.5,
  ) {
    const s = Math.min(1, Math.max(0, skill));
    this.skill = s;
    this.reactionMs = 550 - s * 350;
    this.aimErrMaxDeg = 1.5 + (1 - s) * 5;
    // Same solids the local controller collides against (glass excluded
    // there — the pane is solid-until-shot, tracked via `glassBroken`).
    this.solids = arenaBoxes.map((b) => ({ ...b }));
    this.glassBox = {
      minX: GLASS_PANE_AABB.minX, maxX: GLASS_PANE_AABB.maxX,
      minY: GLASS_PANE_AABB.minY, maxY: GLASS_PANE_AABB.maxY,
      minZ: GLASS_PANE_AABB.minZ, maxZ: GLASS_PANE_AABB.maxZ,
    };
    this.nav = this.buildNav();
  }

  /** Join the practice room (second seat) and start the 30Hz brain. */
  async connect(): Promise<void> {
    const client = new Colyseus.Client(this.serverUrl);
    const room = await client.joinOrCreate("fps_practice", {
      name: this.name,
      practiceId: this.practiceId,
    });
    if (this.disposed) {
      room.leave(true).catch(() => { /* already gone */ });
      return;
    }
    this.room = room;
    console.log(`[practice-bot] joined room ${room.roomId} as ${room.sessionId}`);

    room.onMessage("snapshot", (snap: BotSnapshotMsg) => {
      this.phase = snap.phase;
      this.players = snap.players ?? [];
      this.glassBroken = snap.glassBroken === true;
      // One loadout decision per buy phase; the flag re-arms on any other
      // phase so every round's buy window gets a fresh plan.
      if (this.phase !== "buy") this.boughtThisBuyPhase = false;
      else this.maybeBuy();
      this.ensureWeapon();
    });
    // Scoreboard ping — echo back so the bot shows a sane ping.
    room.onMessage("ping", (msg: unknown) => room.send("pong", msg));
    // Swallow everything else (grenades, zones, equips, …) without warnings.
    room.onMessage("*", () => { /* not the bot's concern */ });
    room.onError(() => { /* room errors surface via onLeave */ });
    room.onLeave(() => this.stopBrain());

    this.brainTimer = window.setInterval(() => this.think(), 33);
  }

  /** Leave the room (consented → forfeits to the human) and stop thinking. */
  dispose(): void {
    this.disposed = true;
    this.stopBrain();
    this.room?.leave(true).catch(() => { /* socket already gone */ });
    this.room = null;
  }

  private stopBrain(): void {
    if (this.brainTimer !== null) {
      window.clearInterval(this.brainTimer);
      this.brainTimer = null;
    }
  }

  // ── economy: one loadout decision per buy phase ──────────────────
  // Mirrors a sane CS:GO wallet: full-buy (rifle + kevlar/helmet) when
  // rich, SMG half-buy in the mid range, Deagle anti-eco below that, a
  // lone pistol-round vest when that's all the money covers, full save
  // otherwise. The server auto-equips each purchase and handles the
  // primary/secondary slot swap + helmet-only top-up pricing, so the bot
  // just sends the buy list in order.
  private maybeBuy(): void {
    if (this.boughtThisBuyPhase) return;
    const room = this.room;
    const me = this.players.find((p) => p.sessionId === room?.sessionId);
    if (!room || !me) return;
    this.boughtThisBuyPhase = true;

    const owned = me.ownedWeapons ?? [];
    const hasVest = (me.armor ?? 0) > 0;
    const hasHelmet = me.helmet === true;
    const fullArmor = hasVest && hasHelmet;
    let budget = me.money ?? 0;

    const isT = me.team !== "ct";
    const rifleId = isT ? "ak47" : "m4a1_s";
    const smgId = isT ? "mac10" : "mp9";
    const cost = (id: string): number => {
      try { return getWeapon(id).cost; } catch { return Number.POSITIVE_INFINITY; }
    };
    const VEST = 650, VEST_HELMET = 1000;
    const ownsPrimary = owned.some((id) => weaponSlotFor(id) === "primary");
    const ownsRifle = owned.some((id) => {
      try { return weaponSlotFor(id) === "primary" && getWeapon(id).class === "rifle"; } catch { return false; }
    });

    const buys: string[] = [];
    const take = (id: string, price: number): void => {
      buys.push(id);
      budget -= price;
    };

    // Weapon tier first (a gun in hand beats armor on an eco), armor next.
    // The rifle branch also covers the SMG→rifle upgrade when rich — the
    // server's primary-slot swap drops the old SMG on purchase.
    if (!ownsRifle && budget >= cost(rifleId) + VEST_HELMET) {
      take(rifleId, cost(rifleId)); // full buy
    } else if (!ownsPrimary && budget >= cost(smgId) + VEST) {
      take(smgId, cost(smgId)); // half buy
    } else if (!ownsPrimary && !owned.includes("deagle") && budget >= cost("deagle") + VEST) {
      take("deagle", cost("deagle")); // anti-eco upgrade over the starter
    }
    // Armor with what's left: helmet included when affordable (the server
    // charges only the $350 top-up if the vest is already full).
    if (!fullArmor && budget >= VEST_HELMET) take("vesthelmet", VEST_HELMET);
    else if (!hasVest && budget >= VEST) take("vest", VEST);

    for (const id of buys) {
      try {
        room.send("buy_weapon", { weaponId: id });
      } catch { /* room gone — onLeave stops the brain */ }
    }
    if (buys.length > 0) console.log(`[practice-bot] buy: ${buys.join(", ")} ($${me.money ?? 0} → ~$${budget})`);
  }

  // ── weapon self-heal (round resets re-grant the starter pistol) ──
  // Equip the best weapon the bot owns; the priority list keeps rifles
  // over SMGs over pistols, so a surviving loadout carries into the next
  // round and a fresh reset falls back to the side starter naturally.
  private desiredWeapon(me: BotPlayerSnap): string {
    const owned = me.ownedWeapons ?? [];
    for (const id of WEAPON_PRIORITY) {
      if (owned.includes(id)) return id;
    }
    return me.team === "ct" ? "usp_s" : "g18";
  }

  private ensureWeapon(): void {
    const me = this.players.find((p) => p.sessionId === this.room?.sessionId);
    if (!me || this.phase === "end") return;
    const want = this.desiredWeapon(me);
    if (me.weaponId === want) return;
    const now = Date.now();
    if (now - this.lastEquipSentAt < 1500) return;
    this.lastEquipSentAt = now;
    try {
      this.room?.send("weapon_equip", { weaponId: want });
    } catch { /* room gone — onLeave stops the brain */ }
  }

  // ── geometry: segment vs AABB (slab method) + eye-to-chest LOS ──
  private segmentBlocked(
    x0: number, y0: number, z0: number,
    x1: number, y1: number, z1: number,
    boxes: Box[],
  ): boolean {
    const dx = x1 - x0, dy = y1 - y0, dz = z1 - z0;
    for (const b of boxes) {
      let tmin = 0, tmax = 1, hit = true;
      const slabs: Array<[number, number, number, number]> = [
        [x0, dx, b.minX, b.maxX],
        [y0, dy, b.minY, b.maxY],
        [z0, dz, b.minZ, b.maxZ],
      ];
      for (const [o, d, lo, hi] of slabs) {
        if (Math.abs(d) < 1e-9) {
          if (o < lo || o > hi) { hit = false; break; }
          continue;
        }
        let t0 = (lo - o) / d, t1 = (hi - o) / d;
        if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
        tmin = Math.max(tmin, t0);
        tmax = Math.min(tmax, t1);
        if (tmin > tmax) { hit = false; break; }
      }
      if (hit) return true;
    }
    return false;
  }

  private hasLos(me: BotPlayerSnap, foe: BotPlayerSnap): boolean {
    if (this.solids.length === 0) return true;
    const boxes = this.glassBroken ? this.solids : [...this.solids, this.glassBox];
    return !this.segmentBlocked(me.x, me.y + 1.7, me.z, foe.x, foe.y + 1.2, foe.z, boxes);
  }

  // ── nav grid: walkability + 8-connected BFS over the arena ──
  private buildNav() {
    const CELL = 0.5;
    const MIN_X = -40, MAX_X = 40, MIN_Z = -25, MAX_Z = 25;
    const W = Math.round((MAX_X - MIN_X) / CELL);
    const H = Math.round((MAX_Z - MIN_Z) / CELL);
    const walkable = new Uint8Array(W * H).fill(1);
    // A box blocks a standing player when it pokes into the body's vertical
    // span: too tall to auto-step onto (maxY > 0.7) and too low to walk
    // under standing (minY < 1.75). Inflate by player radius + margin.
    const INFLATE = 0.3 + 0.15;
    for (const b of [...this.solids, this.glassBox]) {
      if (!(b.maxY > 0.7 && b.minY < 1.75)) continue;
      const x0 = Math.max(0, Math.floor((b.minX - INFLATE - MIN_X) / CELL));
      const x1 = Math.min(W - 1, Math.floor((b.maxX + INFLATE - MIN_X) / CELL));
      const z0 = Math.max(0, Math.floor((b.minZ - INFLATE - MIN_Z) / CELL));
      const z1 = Math.min(H - 1, Math.floor((b.maxZ + INFLATE - MIN_Z) / CELL));
      for (let gz = z0; gz <= z1; gz++)
        for (let gx = x0; gx <= x1; gx++) walkable[gz * W + gx] = 0;
    }
    const toCell = (x: number, z: number) => ({
      gx: Math.min(W - 1, Math.max(0, Math.round((x - MIN_X) / CELL))),
      gz: Math.min(H - 1, Math.max(0, Math.round((z - MIN_Z) / CELL))),
    });
    const toWorld = (gx: number, gz: number) => ({ x: MIN_X + gx * CELL, z: MIN_Z + gz * CELL });
    const free = (gx: number, gz: number) =>
      gx >= 0 && gz >= 0 && gx < W && gz < H && walkable[gz * W + gx] === 1;
    /** Nearest walkable cell to (gx, gz) — spiral out a few rings. */
    function nearestFree(gx: number, gz: number): { gx: number; gz: number } | null {
      if (free(gx, gz)) return { gx, gz };
      for (let r = 1; r <= 8; r++)
        for (let dz = -r; dz <= r; dz++)
          for (let dx = -r; dx <= r; dx++) {
            if (Math.max(Math.abs(dx), Math.abs(dz)) !== r) continue;
            if (free(gx + dx, gz + dz)) return { gx: gx + dx, gz: gz + dz };
          }
      return null;
    }
    /** 8-connected BFS (no diagonal corner-cutting). Returns world waypoints. */
    function findPath(fromX: number, fromZ: number, toX: number, toZ: number) {
      const s = nearestFree(toCell(fromX, fromZ).gx, toCell(fromX, fromZ).gz);
      const t = nearestFree(toCell(toX, toZ).gx, toCell(toX, toZ).gz);
      if (!s || !t) return null;
      const prev = new Int32Array(W * H).fill(-1);
      const startIdx = s.gz * W + s.gx;
      const targetIdx = t.gz * W + t.gx;
      prev[startIdx] = startIdx;
      const queue = [startIdx];
      let head = 0;
      const DIRS = [[1, 0], [-1, 0], [0, 1], [0, -1], [1, 1], [1, -1], [-1, 1], [-1, -1]];
      while (head < queue.length) {
        const cur = queue[head++];
        if (cur === targetIdx) break;
        const cgx = cur % W, cgz = (cur / W) | 0;
        for (const [dx, dz] of DIRS) {
          const nx = cgx + dx, nz = cgz + dz;
          if (!free(nx, nz)) continue;
          if (dx !== 0 && dz !== 0 && (!free(cgx + dx, cgz) || !free(cgx, cgz + dz))) continue;
          const ni = nz * W + nx;
          if (prev[ni] !== -1) continue;
          prev[ni] = cur;
          queue.push(ni);
        }
      }
      if (prev[targetIdx] === -1) return null;
      const path: Array<{ x: number; z: number }> = [];
      for (let cur = targetIdx; cur !== startIdx; cur = prev[cur])
        path.push(toWorld(cur % W, (cur / W) | 0));
      path.reverse();
      return path;
    }
    return { findPath };
  }

  // ── brain: 30Hz input frames ──
  // The server keeps the last input active between ticks, so 30Hz is smooth.
  // Yaw ALWAYS stares at the player; locomotion picks 8-way key combos
  // relative to that stare (same math as bot.mjs — see its comments).
  private think(): void {
    const room = this.room;
    if (!room) return;
    this.seq++;
    const me = this.players.find((p) => p.sessionId === room.sessionId);
    const foe = this.players.find((p) => p.sessionId !== room.sessionId);

    let forward = false, back = false, left = false, right = false, jump = false;
    let actionFlags = 0;

    if (me && foe) {
      const dx = foe.x - me.x;
      const dz = foe.z - me.z;
      const dist = Math.hypot(dx, dz);
      const now = Date.now();

      // The stare — always look at the player; pitch tracks the chest.
      if (dist > 0.05) {
        this.yaw = Math.atan2(-dx, -dz) * RAD2DEG;
        this.pitch = Math.atan2(foe.y + 1.2 - (me.y + 1.7), dist) * RAD2DEG;
      }

      const bothAlive = me.health > 0 && foe.health > 0;
      const canMove = MOVE_PHASES.has(this.phase) && me.health > 0;
      const inCombat = FIRE_PHASES.has(this.phase) && bothAlive;
      const los = bothAlive && this.hasLos(me, foe);
      if (los && inCombat) {
        if (this.losSince === null) {
          this.losSince = now;
          this.errYaw = gauss() * this.aimErrMaxDeg * 1.5; // sloppy first sight
          this.errPitch = gauss() * this.aimErrMaxDeg * 0.75;
        }
      } else {
        this.losSince = null;
      }

      let moveTarget: { x: number; z: number; direct?: boolean } | null = null;

      // Engagement range + tap cadence follow the equipped weapon (a rifle
      // opens fire farther and taps faster than the starter pistol).
      const fire = fireParamsFor(me.weaponId, this.skill);

      if (inCombat && los && dist <= fire.range) {
        // ── firefight: wandering aim error on top of the perfect stare ──
        this.errYaw = Math.max(-this.aimErrMaxDeg, Math.min(this.aimErrMaxDeg, this.errYaw * 0.92 + gauss() * this.aimErrMaxDeg * 0.35));
        this.errPitch = Math.max(-this.aimErrMaxDeg, Math.min(this.aimErrMaxDeg, this.errPitch * 0.92 + gauss() * this.aimErrMaxDeg * 0.2));
        this.yaw += this.errYaw;
        this.pitch += this.errPitch;

        if (me.weaponId === "knife") {
          // Round reset handed us the knife — chase and slash until the
          // pistol re-equip (ensureWeapon) lands.
          if (dist > 1.5) moveTarget = { x: foe.x, z: foe.z, direct: true };
          if (dist < 2.2 && now - (this.losSince ?? now) > this.reactionMs && now >= this.nextShotAt) {
            actionFlags |= ACTION.KNIFE_SLASH;
            this.nextShotAt = now + 600;
          }
        } else {
          // Gunfight: tap-fire once reaction time has passed, strafe the band.
          if (now - (this.losSince ?? now) > this.reactionMs && now >= this.nextShotAt) {
            actionFlags |= ACTION.SHOOT;
            const gap = 1000 / fire.tapsPerSec;
            this.nextShotAt = now + gap * (0.8 + Math.random() * 0.4);
          }
          if (now >= this.nextStrafeFlipAt) {
            this.strafeSide = -this.strafeSide;
            this.nextStrafeFlipAt = now + 600 + Math.random() * 900;
          }
          if (canMove) {
            if (dist > COMBAT_FAR) forward = true;
            else if (dist < COMBAT_NEAR) back = true;
            if (this.strafeSide > 0) right = true;
            else left = true;
          }
        }
      } else if (bothAlive && canMove && dist > 2) {
        // ── approach: BFS through the arena to the player ──
        moveTarget = { x: foe.x, z: foe.z };
      }

      if (moveTarget && canMove) {
        let tx = moveTarget.x, tz = moveTarget.z;
        if (!moveTarget.direct) {
          if (now - this.lastPathAt > 500) {
            this.lastPathAt = now;
            this.path = this.nav.findPath(me.x, me.z, foe.x, foe.z);
          }
          if (this.path) {
            while (this.path.length > 1 && Math.hypot(this.path[0].x - me.x, this.path[0].z - me.z) < 1.2) this.path.shift();
            if (this.path.length > 0) ({ x: tx, z: tz } = this.path[0]);
          }
        }
        const wdx = tx - me.x, wdz = tz - me.z;
        const wlen = Math.hypot(wdx, wdz);
        if (wlen > 0.05) {
          const yr = this.yaw * DEG2RAD;
          const fx = -Math.sin(yr), fz = -Math.cos(yr); // stare-forward
          const rx = Math.cos(yr), rz = -Math.sin(yr); // stare-right
          const a = (wdx * fx + wdz * fz) / wlen; // forward component
          const b = (wdx * rx + wdz * rz) / wlen; // right component
          forward = a > 0.38;
          back = a < -0.38;
          right = b > 0.38;
          left = b < -0.38;
        }

        // Unstuck fallback on top of whatever the path said: pressing into a
        // wall wall-slides already; dead-perpendicular contact gets a jump +
        // alternating-side strafe episode.
        if (!this.anchor || Math.hypot(me.x - this.anchor.x, me.z - this.anchor.z) > 0.35) {
          this.anchor = { x: me.x, z: me.z, t: now };
          this.episode = null;
        } else if (!this.episode && now - this.anchor.t > 900) {
          this.episodeCount++;
          this.episode = {
            until: now + 700,
            jumpUntil: now + 140,
            side: this.episodeCount % 2 === 0 ? "left" : "right",
          };
          this.anchor.t = now; // give this episode its own 900ms before the next
          this.path = null; // force a fresh plan after the wiggle
        }
        if (this.episode) {
          if (now > this.episode.until) {
            this.episode = null;
          } else {
            jump = now < this.episode.jumpUntil;
            left = this.episode.side === "left";
            right = this.episode.side === "right";
            forward = true;
            back = false;
          }
        }
      } else if (!moveTarget) {
        this.anchor = null;
        this.episode = null;
      }
    }

    try {
      room.send("input", {
        seq: this.seq,
        forward,
        back,
        left,
        right,
        jump,
        crouch: false,
        walk: false,
        yaw: this.yaw,
        pitch: this.pitch,
        dPitch: 0,
        dYaw: 0,
        actionFlags,
      });
    } catch { /* room closed — onLeave stops the brain */ }
  }
}
